/**
 * Limites das colunas que o POST /restaurant/{id}/order grava a partir do payload — a metade FRONT do
 * contrato `contracts/pedido-colunas.json` do server (cópia em `src/testing/contracts/pedido-colunas.json`).
 *
 * Por que existe: em 11/10/2026 o !2072 passou a mandar a query string do link em
 * `details.externalSource` cortada em 255, uma constante escrita à mão. A coluna era varchar(191), o
 * MySQL em modo strict recusou o insert (1406 Data too long) e ~3.044 pedidos de 202 lojas morreram em
 * 3 h. O teste do front validou o corte contra a mesma constante chutada, e passou.
 *
 * Regras:
 *  - Corte de campo do pedido NUNCA usa número próprio: lê `limiteDoPedido('<campo>')`.
 *  - Este mapa é o MENOR limite entre as colunas que o campo alimenta (orders e clients), e o
 *    `order-payload-limits.spec.ts` reprova se ele divergir da cópia do contrato.
 *  - Quando uma migration mudar a coluna (ex.: external_source 191 → 1024), atualize a cópia do
 *    contrato e este mapa SÓ DEPOIS de a migration estar em produção: o front que corta acima da
 *    coluna real é exatamente o incidente de 11/10.
 *  - Limite em CARACTERES (utf8mb4), como o MySQL conta varchar — `Array.from` respeita emoji.
 */
export const LIMITES_DO_PEDIDO: Readonly<Record<string, number>> = Object.freeze({
    'details.externalSource': 1024,
    'details.campaign': 191,
    'details.order_notes': 191,
    'details.table': 191,
    'details.source': 191,
    'details.deliveryType': 191,
    'details.status': 50,
    'details.paymentType': 191,
    'details.cardType': 191,
    'details.cardMethod': 191,
    'details.payments[].paymentType': 191,
    'details.payments[].cardType': 191,
    'details.payments[].cardPaymentMethod': 191,
    'user.name': 191,
    'user.phone': 191,
    'user.address': 255,
    'user.number': 191,
    'user.zone': 191,
    'user.city': 191,
    'user.email': 191,
    'user.cpf': 191,
    'user.car_plate': 191,
    'user.car_model_color': 191,
    'user.one_signal_id': 191,
    'user.country': 191,
    'user.complemento': 65535,
    'user.address_ref': 65535,
    'orders[].notes': 65535,
});

export function limiteDoPedido(campo: string): number {
    const limite = LIMITES_DO_PEDIDO[campo];
    if (!limite) throw new Error(`campo do pedido sem limite no contrato: ${campo}`);
    return limite;
}

/** Corta em caracteres (não em unidades UTF-16): emoji não vira meia surrogate. */
export function cortarEmCaracteres(valor: string, limite: number): string {
    const chars = Array.from(valor);
    return chars.length <= limite ? valor : chars.slice(0, limite).join('');
}

/**
 * Fronteira do envio: os campos que vêm do LINK (não da digitação do cliente) cabem na coluna antes
 * de sair. Eles são opcionais — a falta deles não estraga o pedido; o excesso, sem esta guarda,
 * estragava o pedido inteiro.
 *  - externalSource (query de anúncio, `?src=`): corta no limite — o começo da query é o que importa.
 *  - campaign (uuid da lista de transmissão, chave de junção): maior que a coluna não é uuid válido,
 *    então sai fora em vez de ir cortado.
 * Os campos digitados pelo cliente ficam com o `maxlength` das telas e com a guarda do server
 * (classificados no spec).
 */
export function ajustarOrigemAoContrato(details: { externalSource?: string | null; campaign?: string | null }): void {
    if (typeof details.externalSource === 'string') {
        details.externalSource = cortarEmCaracteres(details.externalSource, limiteDoPedido('details.externalSource'));
    }
    if (typeof details.campaign === 'string' && Array.from(details.campaign).length > limiteDoPedido('details.campaign')) {
        details.campaign = null;
    }
}
